// src/features/permissions/ViewPermissions.jsx
import { useState, useEffect, useMemo } from 'react';
import {
  Card,
  Form,
  Button,
  Alert,
  Spinner,
  Badge
} from 'react-bootstrap';
import {
  Shield,
  Search,
  Eye,
  Lock,
  Package,
  DollarSign,
  BarChart3,
  UsersRound,
  Layers,
  ChevronDown,
  ArrowLeft,
  CheckCircle,
  XCircle,
  Info
} from 'lucide-react';
import { useNavigate, useLocation } from 'react-router-dom';
import roleManagementService from '../../services/roleManagementService';
import permissionService from '../../services/permissionService';
import './ViewPermissions.css';

// Feature -> icon map
const FEATURE_ICONS = {
  Inventory: Package,
  Payroll: DollarSign,
  Reports: BarChart3,
  HR: UsersRound
};

const getFeatureIcon = (featureKey) => FEATURE_ICONS[featureKey] || Layers;

export default function ViewPermissions() {
  const navigate = useNavigate();
  const location = useLocation();

  const { roleId } = location.state || {};
  const [selectedRoleId, setSelectedRoleId] = useState(roleId || '');
  const [roles, setRoles] = useState([]);
  const [permissions, setPermissions] = useState({});
  const [rolePermissions, setRolePermissions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingRoles, setLoadingRoles] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [assignedCount, setAssignedCount] = useState(0);
  const [totalPermissions, setTotalPermissions] = useState(0);

  useEffect(() => {
    loadRoles();
  }, []);

  useEffect(() => {
    if (selectedRoleId) {
      loadPermissionsForRole(selectedRoleId);
    }
  }, [selectedRoleId]);

  const loadRoles = async () => {
    setLoadingRoles(true);
    setErrorMessage('');
    try {
      const result = await roleManagementService.getRoles();
      if (result.success) {
        setRoles(result.data || []);
        if (result.data && result.data.length > 0 && !selectedRoleId) {
          setSelectedRoleId(result.data[0].uuid);
        }
      } else {
        setErrorMessage(result.message || 'Failed to load roles');
      }
    } catch (err) {
      setErrorMessage('Failed to load roles. Please try again.');
    }
    setLoadingRoles(false);
  };

  const loadPermissionsForRole = async (roleId) => {
    setLoading(true);
    setErrorMessage('');
    try {
      // Get grouped permissions
      const permResult = await permissionService.getPermissionsGrouped();
      if (!permResult.success) {
        setErrorMessage(permResult.message || 'Failed to load permissions');
        setLoading(false);
        return;
      }

      // Transform API data to match component structure
      const groupedPermissions = {};
      let total = 0;
      (permResult.data || []).forEach(feature => {
        const featureName = feature.feature?.name || 'Unknown';
        groupedPermissions[featureName] = {
          licensed: feature.is_licensed || false,
          permissions: (feature.permissions || []).map(p => ({
            id: p.uuid,
            code: p.code,
            name: p.name,
            description: p.description
          }))
        };
        total += (feature.permissions || []).length;
      });
      setPermissions(groupedPermissions);
      setTotalPermissions(total);

      // Get role's assigned permissions
      const rolePermResult = await roleManagementService.getRolePermissions(roleId);
      if (rolePermResult.success) {
        const assigned = (rolePermResult.data?.permissions || []).map(p => p.uuid);
        setRolePermissions(assigned);
        setAssignedCount(assigned.length);
      } else {
        setRolePermissions([]);
        setAssignedCount(0);
      }
    } catch (err) {
      setErrorMessage('Failed to load permissions. Please try again.');
    }
    setLoading(false);
  };

  const handleRoleChange = (e) => {
    const roleId = e.target.value;
    setSelectedRoleId(roleId);
  };

  const handleSearch = (e) => {
    setSearchTerm(e.target.value);
  };

  const clearSearch = () => {
    setSearchTerm('');
  };

  const handleBack = () => {
    navigate('/retailone-dashboard/roles');
  };

  // Filter permissions based on search
  const filteredPermissions = useMemo(() => {
    if (!searchTerm.trim()) return permissions;

    const filtered = {};
    Object.entries(permissions).forEach(([feature, data]) => {
      const matchedPermissions = data.permissions.filter(p =>
        p.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        p.code?.toLowerCase().includes(searchTerm.toLowerCase())
      );
      if (matchedPermissions.length > 0) {
        filtered[feature] = {
          ...data,
          permissions: matchedPermissions
        };
      }
    });
    return filtered;
  }, [permissions, searchTerm]);

  const selectedRole = roles.find(r => r.uuid === selectedRoleId);

  // Check if permission is assigned
  const isAssigned = (permissionId) => {
    return rolePermissions.includes(permissionId);
  };

  // Get check state for feature
  const getFeatureCheckState = (featurePermissions) => {
    const ids = featurePermissions.map(p => p.id);
    const checked = ids.filter(id => rolePermissions.includes(id));
    if (checked.length === 0) return 'none';
    if (checked.length === ids.length) return 'all';
    return 'partial';
  };

  return (
    <div className="vp-page">
      <div className="vp-shell">
        {/* Header */}
        <div className="vp-header">
          <div className="vp-header-left">
            <div className="vp-header-icon">
              <Eye size={22} color="white" strokeWidth={2.25} />
            </div>
            <div>
              <span className="vp-eyebrow">Access Control</span>
              <h1 className="vp-title">View Permissions</h1>
              <p className="vp-subtitle">View permissions assigned to each role</p>
            </div>
          </div>
          <div className="vp-header-right">
            <div className="vp-view-indicator">
              <Eye size={14} />
              <span>View Only Mode</span>
            </div>
            <Button
              onClick={handleBack}
              className="vp-back-button"
            >
              <ArrowLeft size={16} />
              Back to Roles
            </Button>
          </div>
        </div>

        {/* Error Alert */}
        {errorMessage && (
          <Alert variant="danger" className="vp-alert vp-alert-error">
            <XCircle size={18} />
            {errorMessage}
          </Alert>
        )}

        {/* Main Content */}
        <Card className="vp-panel">
          <Card.Body className="vp-panel-body">
            {/* Toolbar */}
            <div className="vp-toolbar">
              <div className="vp-role-selector">
                <label className="vp-role-label">Select Role</label>
                <div className="vp-select-wrapper">
                  <Form.Select
                    value={selectedRoleId}
                    onChange={handleRoleChange}
                    className="vp-select"
                    disabled={loadingRoles || roles.length === 0}
                  >
                    {roles.map((role) => (
                      <option key={role.uuid} value={role.uuid}>
                        {role.name}
                      </option>
                    ))}
                  </Form.Select>
                  <ChevronDown size={16} className="vp-select-chevron" />
                </div>
              </div>

              <div className="vp-search">
                <Search size={17} className="vp-search-icon" />
                <input
                  type="text"
                  placeholder="Search permissions by name or code..."
                  value={searchTerm}
                  onChange={handleSearch}
                  className="vp-search-input"
                  disabled={loading}
                />
                {searchTerm && (
                  <button className="vp-search-clear" onClick={clearSearch}>
                    <XCircle size={16} />
                  </button>
                )}
              </div>
            </div>

            {/* Role Info Bar */}
            {selectedRole && (
              <div className="vp-role-info-bar">
                <div className="vp-role-info-left">
                  <span className="vp-role-info-name">{selectedRole.name}</span>
                  <span className="vp-role-info-desc">{selectedRole.description || 'No description'}</span>
                </div>
                <div className="vp-role-info-right">
                  <span className="vp-role-info-stats">
                    <span className="vp-role-info-stat">
                      <Shield size={14} />
                      {assignedCount} of {totalPermissions} assigned
                    </span>
                  </span>
                </div>
              </div>
            )}

            {/* Permissions List */}
            {loading ? (
              <div className="vp-state">
                <Spinner animation="border" className="vp-state-spinner" />
                <p className="vp-state-title">Loading permissions...</p>
              </div>
            ) : Object.keys(filteredPermissions).length === 0 ? (
              <div className="vp-state">
                {searchTerm ? (
                  <>
                    <Search size={40} className="vp-state-icon" />
                    <p className="vp-state-title">No permissions match your search</p>
                    <p className="vp-state-subtitle">Try adjusting your search terms</p>
                  </>
                ) : (
                  <>
                    <Shield size={40} className="vp-state-icon" />
                    <p className="vp-state-title">No permissions available</p>
                    <p className="vp-state-subtitle">No permissions found for this company</p>
                  </>
                )}
              </div>
            ) : (
              <div className="vp-feature-list">
                {Object.entries(filteredPermissions).map(([featureKey, featureData], index) => {
                  const isLicensed = featureData.licensed;
                  const featurePermissions = featureData.permissions;
                  const checkState = getFeatureCheckState(featurePermissions);
                  const FeatureIcon = getFeatureIcon(featureKey);

                  return (
                    <div
                      key={featureKey}
                      className={`vp-feature ${!isLicensed ? 'vp-feature-locked' : ''}`}
                      style={{ animationDelay: `${index * 0.05}s` }}
                    >
                      {/* Feature Header */}
                      <div className="vp-feature-head">
                        <div className="vp-feature-identity">
                          <div className="vp-feature-icon">
                            <FeatureIcon size={16} strokeWidth={2.25} />
                          </div>
                          <h3 className="vp-feature-name">{featureKey}</h3>
                          
                          {isLicensed ? (
                            <span className="vp-pill-licensed">Licensed</span>
                          ) : (
                            <span className="vp-pill-locked">Not Licensed</span>
                          )}
                          
                          {checkState === 'all' && (
                            <span className="vp-status-all">All assigned</span>
                          )}
                          {checkState === 'partial' && (
                            <span className="vp-status-partial">Partial</span>
                          )}
                          {checkState === 'none' && (
                            <span className="vp-status-none">None assigned</span>
                          )}
                        </div>
                        <div className="vp-feature-select-all">
                          <span className="vp-select-all-label">
                            {checkState === 'all' ? 'All selected' :
                             checkState === 'partial' ? 'Partially selected' :
                             'None selected'}
                          </span>
                        </div>
                      </div>

                      {/* Feature Permissions - Read Only */}
                      <div className="vp-feature-body">
                        {featurePermissions.map((permission) => {
                          const assigned = isAssigned(permission.id);
                          return (
                            <div
                              key={permission.id}
                              className={`vp-item ${assigned ? 'vp-item-assigned' : ''}`}
                            >
                              <div className="vp-item-left">
                                <div className={`vp-item-indicator ${assigned ? 'vp-item-indicator-checked' : 'vp-item-indicator-unchecked'}`}>
                                  {assigned ? (
                                    <CheckCircle size={14} />
                                  ) : (
                                    <XCircle size={14} />
                                  )}
                                </div>
                                <span className="vp-item-text">
                                  <span className="vp-item-name">{permission.name}</span>
                                  <span className="vp-item-code">{permission.code}</span>
                                </span>
                              </div>
                              <div className="vp-item-right">
                                <span className={assigned ? 'vp-item-badge-assigned' : 'vp-item-badge-unassigned'}>
                                  {assigned ? 'Assigned' : 'Not assigned'}
                                </span>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}

            {/* Footer */}
            <div className="vp-footer">
              <div className="vp-footer-left">
                <Info size={14} />
                <span>This is a read-only view. Permissions cannot be modified here.</span>
              </div>
              <div className="vp-footer-right">
                <span className="vp-footer-stat">
                  Total: {totalPermissions} permissions
                </span>
                <span className="vp-footer-divider">•</span>
                <span className="vp-footer-stat">
                  Assigned: {assignedCount}
                </span>
              </div>
            </div>
          </Card.Body>
        </Card>
      </div>
    </div>
  );
}